import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { Copy, Link2, Pencil, Plus, Trash2 } from 'lucide-react'
import { QRCodeSVG } from 'qrcode.react'
import { toast } from 'sonner'

import { CareLogMultiSelect } from './-care-log-multi-select'
import type { ProductClassification } from '@/lib/api/product-classifications-api'
import type { ProductClassificationFormValues } from '@/lib/schemas/product-classification-schemas'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useAuth } from '@/hooks/use-auth'
import {
    useCreateProductClassification,
    useDeleteProductClassification,
    useProductClassification,
    useProductClassifications,
    useUpdateProductClassification,
} from '@/hooks/use-product-classifications-query'
import { useTeaLots } from '@/hooks/use-tea-lots-query'
import { traceCodesApi } from '@/lib/api/trace-codes-api'
import { m } from '@/lib/paraglide/messages'
import { createProductClassificationSchema } from '@/lib/schemas/product-classification-schemas'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/agriculture/product-classifications',
)({
    component: ProductClassificationsPage,
})

function ProductClassificationsPage() {
    const { hasPermission } = useAuth()
    const canWrite = hasPermission('product_classifications.write')
    const canDelete = hasPermission('product_classifications.delete')
    const canMintTrace = hasPermission('trace_codes.write')

    const [editingId, setEditingId] = useState<string | null>(null)
    const [creating, setCreating] = useState(false)
    const [traceInfo, setTraceInfo] = useState<{
        classifId: string
        code: string
    } | null>(null)

    const { data } = useProductClassifications({ per_page: 100 })
    const rows = data?.data ?? []

    // Fetch full detail (includes tea_lots M-N) so edit form hydrates correctly.
    const { data: editingDetail } = useProductClassification(editingId)

    const createMut = useCreateProductClassification()
    const updateMut = useUpdateProductClassification(editingId ?? '')
    const deleteMut = useDeleteProductClassification()

    const schema = createProductClassificationSchema()
    const form = useForm<ProductClassificationFormValues>({
        resolver: zodResolver(schema),
        defaultValues: {
            name: '',
            code: '',
            type: '',
            classified_date: '',
            weight: 0,
            used: 0,
            price: 0,
            tea_lot_ids: [],
        },
    })

    const dialogOpen = creating || (!!editingId && !!editingDetail)

    function openCreate() {
        form.reset({
            name: '',
            code: '',
            type: '',
            classified_date: '',
            weight: 0,
            used: 0,
            price: 0,
            tea_lot_ids: [],
        })
        setCreating(true)
    }

    function openEdit(c: ProductClassification) {
        setEditingId(c.id)
        // Form will be reset inside ClassificationFormDialog once editingDetail loads.
    }

    function closeDialog() {
        setCreating(false)
        setEditingId(null)
        form.reset()
    }

    function onSubmit(values: ProductClassificationFormValues) {
        const payload = {
            name: values.name,
            code: values.code || null,
            type: values.type || null,
            classified_date: values.classified_date || null,
            weight: values.weight,
            used: values.used,
            price: values.price,
            tea_lot_ids: values.tea_lot_ids,
        }
        if (editingId) {
            updateMut.mutate(payload, { onSuccess: closeDialog })
        } else {
            createMut.mutate(payload, { onSuccess: closeDialog })
        }
    }

    function handleDelete(c: ProductClassification) {
        if (!confirm(m.product_classification_delete_confirm())) return
        deleteMut.mutate(c.id)
    }

    async function handleMintTrace(c: ProductClassification) {
        try {
            const res = await traceCodesApi.mint({
                origin_type: 'PRODUCT-CLASSIFICATION',
                origin_id: c.id,
            })
            const code = res.data?.short_code
            if (code) {
                setTraceInfo({ classifId: c.id, code })
            }
        } catch {
            toast.error(m.toast_trace_mint_error())
        }
    }

    const isSaving = createMut.isPending || updateMut.isPending
    const traceUrl = traceInfo
        ? `${window.location.origin}/trace/${traceInfo.code}`
        : ''

    return (
        <div className="p-6 space-y-4">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-semibold">
                    {m.product_classification_title()}
                </h1>
                {canWrite && (
                    <Button onClick={openCreate}>
                        <Plus className="mr-2 h-4 w-4" />
                        {m.product_classification_new()}
                    </Button>
                )}
            </div>

            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead>{m.product_classification_name()}</TableHead>
                        <TableHead>{m.product_classification_code()}</TableHead>
                        <TableHead>{m.product_classification_type()}</TableHead>
                        <TableHead>
                            {m.product_classification_classified_date()}
                        </TableHead>
                        <TableHead>
                            {m.product_classification_weight()}
                        </TableHead>
                        <TableHead>
                            {m.product_classification_price()}
                        </TableHead>
                        <TableHead />
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.map((c) => (
                        <TableRow key={c.id}>
                            <TableCell className="font-medium">
                                {c.name}
                            </TableCell>
                            <TableCell className="font-mono text-sm">
                                {c.code ?? '—'}
                            </TableCell>
                            <TableCell>{c.type ?? '—'}</TableCell>
                            <TableCell>
                                {c.classified_date
                                    ? c.classified_date.slice(0, 10)
                                    : '—'}
                            </TableCell>
                            <TableCell>{c.weight}</TableCell>
                            <TableCell>{c.price.toLocaleString()}</TableCell>
                            <TableCell className="text-right space-x-1">
                                {canMintTrace && (
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={() => handleMintTrace(c)}
                                        title={m.tea_lot_trace_action()}
                                    >
                                        <Link2 className="h-4 w-4" />
                                    </Button>
                                )}
                                {canWrite && (
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => openEdit(c)}
                                    >
                                        <Pencil className="h-4 w-4" />
                                    </Button>
                                )}
                                {canDelete && (
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => handleDelete(c)}
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                )}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            {/* Create / Edit dialog */}
            {dialogOpen && (
                <ClassificationFormDialog
                    key={editingId ?? 'new'}
                    open={dialogOpen}
                    title={
                        editingId
                            ? m.product_classification_edit()
                            : m.product_classification_new()
                    }
                    initial={editingId ? (editingDetail ?? null) : null}
                    form={form}
                    onSubmit={onSubmit}
                    isSaving={isSaving}
                    onClose={closeDialog}
                />
            )}

            {/* Trace code dialog */}
            {traceInfo && (
                <Dialog
                    open
                    onOpenChange={(o) => {
                        if (!o) setTraceInfo(null)
                    }}
                >
                    <DialogContent className="max-w-sm">
                        <DialogHeader>
                            <DialogTitle>
                                {m.trace_code_dialog_title()}
                            </DialogTitle>
                        </DialogHeader>
                        <div className="space-y-3">
                            <div className="space-y-1">
                                <Label className="text-xs text-muted-foreground">
                                    {m.trace_code_label()}
                                </Label>
                                <p className="font-mono text-lg font-semibold">
                                    {traceInfo.code}
                                </p>
                            </div>
                            <div className="space-y-1">
                                <Label className="text-xs text-muted-foreground">
                                    {m.trace_url_label()}
                                </Label>
                                <div className="flex items-center gap-2">
                                    <p className="text-sm text-muted-foreground truncate flex-1">
                                        {traceUrl}
                                    </p>
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={() => {
                                            void navigator.clipboard.writeText(
                                                traceUrl,
                                            )
                                            toast.success(m.toast_copied())
                                        }}
                                    >
                                        <Copy className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                            <div className="flex justify-center pt-1">
                                <QRCodeSVG value={traceUrl} size={160} />
                            </div>
                        </div>
                    </DialogContent>
                </Dialog>
            )}
        </div>
    )
}

function ClassificationFormDialog({
    open,
    title,
    initial,
    form,
    onSubmit,
    isSaving,
    onClose,
}: {
    open: boolean
    title: string
    initial: ProductClassification | null
    form: ReturnType<typeof useForm<ProductClassificationFormValues>>
    onSubmit: (v: ProductClassificationFormValues) => void
    isSaving: boolean
    onClose: () => void
}) {
    // Reset form when initial data loads (edit). key prop on ClassificationFormDialog
    // handles remounting so this fires once per edit session.
    useEffect(() => {
        if (initial) {
            form.reset({
                name: initial.name,
                code: initial.code ?? '',
                type: initial.type ?? '',
                classified_date: initial.classified_date?.slice(0, 10) ?? '',
                weight: initial.weight,
                used: initial.used,
                price: initial.price,
                // TeaLots hydrated by getById; map to IDs for the M-N multiselect.
                tea_lot_ids: initial.tea_lots?.map((l) => l.id) ?? [],
            })
        }
    }, [initial, form])

    const { data: teaLotsData } = useTeaLots({ per_page: 200 })
    const teaLots = teaLotsData?.data ?? []
    const teaLotOptions = useMemo(
        () =>
            teaLots.map((l) => ({
                value: l.id,
                label: `${l.lot_date.slice(0, 10)} — ${l.quantity} kg`,
            })),
        [teaLots],
    )

    return (
        <Dialog
            open={open}
            onOpenChange={(o) => {
                if (!o) onClose()
            }}
        >
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                </DialogHeader>
                <form
                    id="classification-form"
                    onSubmit={form.handleSubmit(onSubmit)}
                    className="space-y-4"
                >
                    <div className="space-y-1">
                        <Label>{m.product_classification_name()}</Label>
                        <Input {...form.register('name')} />
                        {form.formState.errors.name && (
                            <p className="text-destructive text-sm">
                                {form.formState.errors.name.message}
                            </p>
                        )}
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                            <Label>{m.product_classification_code()}</Label>
                            <Input {...form.register('code')} />
                        </div>
                        <div className="space-y-1">
                            <Label>{m.product_classification_type()}</Label>
                            <Input {...form.register('type')} />
                        </div>
                    </div>

                    <div className="space-y-1">
                        <Label>
                            {m.product_classification_classified_date()}
                        </Label>
                        <Input
                            type="date"
                            {...form.register('classified_date')}
                        />
                    </div>

                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                        <div className="space-y-1">
                            <Label>{m.product_classification_weight()}</Label>
                            <Input
                                type="number"
                                step="0.01"
                                min="0"
                                {...form.register('weight')}
                            />
                        </div>
                        <div className="space-y-1">
                            <Label>{m.product_classification_used()}</Label>
                            <Input
                                type="number"
                                step="0.01"
                                min="0"
                                {...form.register('used')}
                            />
                        </div>
                        <div className="space-y-1">
                            <Label>{m.product_classification_price()}</Label>
                            <Input
                                type="number"
                                step="1"
                                min="0"
                                {...form.register('price')}
                            />
                        </div>
                    </div>

                    <div className="space-y-1">
                        <Label>{m.product_classification_tea_lots()}</Label>
                        <CareLogMultiSelect
                            options={teaLotOptions}
                            selected={form.watch('tea_lot_ids')}
                            onChange={(v) => form.setValue('tea_lot_ids', v)}
                            placeholder={m.product_classification_tea_lots_placeholder()}
                        />
                    </div>
                </form>
                <DialogFooter>
                    <Button
                        form="classification-form"
                        type="submit"
                        disabled={isSaving}
                    >
                        {m.product_classification_save()}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
